import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, CaretRight } from "@phosphor-icons/react";
import { track } from "@/lib/track";

const EM = "text-emerald-600";
const Overline = ({ children }) => (
  <p className={`overline-label ${EM} mb-5`}>{children}</p>
);
const H = ({ children, className = "" }) => (
  <h2 className={`font-heading font-extrabold text-slate-900 tracking-tight leading-[1.08] text-3xl sm:text-4xl lg:text-5xl ${className}`}>{children}</h2>
);
const Sub = ({ children, className = "" }) => (
  <p className={`text-slate-600 text-base md:text-lg leading-relaxed mt-6 max-w-2xl ${className}`}>{children}</p>
);
const Card = ({ k, title, body }) => (
  <div className="border border-slate-200 bg-white shadow-sm p-5">
    {k && <p className={`font-mono-data text-[11px] ${EM} mb-2`}>{k}</p>}
    <p className="font-heading font-bold text-slate-900 text-sm">{title}</p>
    {body && <p className="text-slate-600 text-xs leading-relaxed mt-1.5">{body}</p>}
  </div>
);
const Li = ({ t, d }) => (
  <li className="flex items-start gap-3">
    <CaretRight size={13} className={`${EM} mt-1 shrink-0`} weight="bold" />
    <p className="text-sm text-slate-700 leading-relaxed"><span className="text-slate-900 font-semibold">{t}</span>{d && <span className="text-slate-600"> — {d}</span>}</p>
  </li>
);
const Wordmark = () => (
  <p className="font-heading font-extrabold text-slate-900 text-2xl tracking-tight">MAI MATRIX<span className={EM}>™</span></p>
);

/* ---------- slide graphics (pure SVG, on-brand) ---------- */
const G = { grid: "rgba(15,23,42,0.12)", dim: "#64748B", txt: "#475569", em: "#059669", emDark: "#047857", amber: "#D97706", red: "#DC2626" };

const ScatterGraphic = () => {
  const depts = [["Clinical", 40, 40], ["HEOR", 300, 34], ["Medical Affairs", 60, 210], ["Affiliates", 310, 200], ["Regulatory", 180, 120]];
  const dots = [[95,70],[130,52],[70,95],[350,70],[320,95],[385,55],[110,235],[85,185],[150,225],[355,170],[330,240],[390,215],[225,95],[160,150],[250,150],[205,175],[275,60],[55,140],[400,140],[240,230]];
  return (
    <svg viewBox="0 0 440 280" className="w-full max-w-md" role="img" aria-label="Evidence scattered across departments">
      {dots.map(([x, y], i) => (
        <g key={i}>
          <line x1={x} y1={y} x2={dots[(i + 7) % dots.length][0]} y2={dots[(i + 7) % dots.length][1]} stroke={G.grid} strokeDasharray="3 5" />
          <circle cx={x} cy={y} r="4" fill={i % 4 === 0 ? G.amber : i % 5 === 0 ? G.red : G.dim} opacity="0.85" />
        </g>
      ))}
      {depts.map(([n, x, y]) => (
        <g key={n}>
          <rect x={x - 8} y={y - 14} width={n.length * 6.4 + 16} height="20" fill="rgba(15,23,42,0.03)" stroke={G.grid} />
          <text x={x} y={y} fill={G.txt} fontSize="10" fontFamily="monospace">{n}</text>
        </g>
      ))}
      <text x="220" y="270" fill={G.dim} fontSize="9" fontFamily="monospace" textAnchor="middle">the same evidence · five owners · no single picture</text>
    </svg>
  );
};

const TrendGraphic = () => (
  <svg viewBox="0 0 440 280" className="w-full max-w-md" role="img" aria-label="Requirements rising while launch windows shrink">
    {[60, 110, 160, 210].map((y) => <line key={y} x1="40" y1={y} x2="420" y2={y} stroke={G.grid} />)}
    <line x1="40" y1="240" x2="420" y2="240" stroke={G.dim} />
    <polyline points="40,200 120,185 200,150 280,110 360,75 420,55" fill="none" stroke={G.em} strokeWidth="2.5" />
    <polyline points="40,90 120,105 200,130 280,160 360,190 420,205" fill="none" stroke={G.red} strokeWidth="2.5" strokeDasharray="6 4" />
    <circle cx="420" cy="55" r="4" fill={G.em} /><circle cx="420" cy="205" r="4" fill={G.red} />
    <text x="40" y="258" fill={G.dim} fontSize="9" fontFamily="monospace">2010</text>
    <text x="400" y="258" fill={G.dim} fontSize="9" fontFamily="monospace">2026</text>
    <text x="46" y="46" fill={G.em} fontSize="10" fontFamily="monospace">HTA evidence requirements per market ↑</text>
    <text x="46" y="78" fill={G.red} fontSize="10" fontFamily="monospace">time allowed to get ready ↓</text>
  </svg>
);

const RadarGraphic = () => {
  const domains = [["Evidence", 64], ["Coverage", 38], ["Reusability", 98], ["Submission", 72], ["Economic", 58], ["Regulatory", 80], ["Pricing", 55], ["GVD", 61]];
  const cx = 150, cy = 140, R = 100;
  const pt = (i, r) => {
    const a = (Math.PI * 2 * i) / 8 - Math.PI / 2;
    return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
  };
  const ring = (r) => domains.map((_, i) => pt(i, r).join(",")).join(" ");
  const val = domains.map(([, v], i) => pt(i, (v / 100) * R).join(",")).join(" ");
  return (
    <svg viewBox="0 0 320 300" className="w-full max-w-sm" role="img" aria-label="GMARI eight-domain radar">
      {[0.33, 0.66, 1].map((f) => <polygon key={f} points={ring(R * f)} fill="none" stroke={G.grid} />)}
      {domains.map((_, i) => { const [x, y] = pt(i, R); return <line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke={G.grid} />; })}
      <polygon points={val} fill="rgba(5,150,105,0.18)" stroke={G.em} strokeWidth="2" />
      {domains.map(([n, v], i) => {
        const [x, y] = pt(i, R + 22);
        return <text key={n} x={x} y={y} fill={G.txt} fontSize="9.5" fontFamily="monospace" textAnchor="middle">{n} {v}</text>;
      })}
      <text x={cx} y={cy - 2} fill="#0F172A" fontSize="26" fontWeight="800" textAnchor="middle" fontFamily="sans-serif">65</text>
      <text x={cx} y={cy + 16} fill={G.em} fontSize="9" fontFamily="monospace" textAnchor="middle">GMARI™ / 100</text>
    </svg>
  );
};

const WaveGraphic = () => {
  const rows = [["India", 0, 22, 1], ["Singapore", 4, 30, 1], ["Australia", 10, 44, 2], ["Germany", 22, 62, 2], ["Japan", 30, 78, 3], ["China", 40, 92, 3]];
  const cols = { 1: G.em, 2: G.amber, 3: G.dim };
  return (
    <svg viewBox="0 0 440 250" className="w-full max-w-md" role="img" aria-label="Wave-sequenced submission roadmap">
      {[100, 200, 300, 400].map((x, i) => (
        <g key={x}><line x1={x} y1="16" x2={x} y2="204" stroke={G.grid} />
          <text x={x} y="222" fill={G.dim} fontSize="9" fontFamily="monospace" textAnchor="middle">{`M${(i + 1) * 12}`}</text></g>
      ))}
      {rows.map(([n, s, e, w], i) => {
        const y = 24 + i * 30;
        return (
          <g key={n}>
            <text x="92" y={y + 11} fill={G.txt} fontSize="10" fontFamily="monospace" textAnchor="end">{n}</text>
            <rect x={100 + s * 3.2} y={y} width={(e - s) * 3.2} height="15" fill={cols[w]} opacity={w === 3 ? 0.55 : 0.9} rx="1" />
          </g>
        );
      })}
      <g fontFamily="monospace" fontSize="9">
        <rect x="100" y="232" width="10" height="8" fill={G.em} /><text x="115" y="240" fill={G.txt}>Wave 1 · submit today</text>
        <rect x="240" y="232" width="10" height="8" fill={G.amber} /><text x="255" y="240" fill={G.txt}>Wave 2 · localize</text>
        <rect x="360" y="232" width="10" height="8" fill={G.dim} /><text x="375" y="240" fill={G.txt}>Wave 3 · build</text>
      </g>
    </svg>
  );
};

const QuadrantGraphic = () => (
  <svg viewBox="0 0 440 300" className="w-full max-w-md" role="img" aria-label="Positioning map: AI scale vs expert governance">
    <line x1="220" y1="20" x2="220" y2="260" stroke={G.grid} />
    <line x1="30" y1="140" x2="410" y2="140" stroke={G.grid} />
    <text x="220" y="14" fill={G.txt} fontSize="9" fontFamily="monospace" textAnchor="middle">expert judgment &amp; governance ↑</text>
    <text x="404" y="154" fill={G.txt} fontSize="9" fontFamily="monospace" textAnchor="end">AI scale →</text>
    <g fontFamily="monospace" fontSize="10">
      <circle cx="110" cy="70" r="5" fill={G.dim} /><text x="110" y="55" fill={G.txt} textAnchor="middle">Traditional consulting</text>
      <circle cx="90" cy="215" r="5" fill={G.dim} /><text x="90" y="238" fill={G.txt} textAnchor="middle">Repositories / SharePoint</text>
      <circle cx="330" cy="220" r="5" fill={G.dim} /><text x="330" y="243" fill={G.txt} textAnchor="middle">Generic AI copilots</text>
      <circle cx="340" cy="62" r="9" fill={G.em} />
      <circle cx="340" cy="62" r="15" fill="none" stroke={G.em} opacity="0.4" />
      <text x="340" y="40" fill="#0F172A" fontWeight="700" textAnchor="middle">MAI Matrix™</text>
    </g>
    <text x="220" y="290" fill={G.dim} fontSize="9" fontFamily="monospace" textAnchor="middle">AI-scale analysis, expert-governed conclusions — alone in the upper right</text>
  </svg>
);

const SLIDES = [
  { label: "Corporate briefing", render: () => (
    <div className="max-w-4xl">
      <Wordmark />
      <h1 className="font-heading font-extrabold text-slate-900 tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl mt-10">
        Are we market access ready?<br /><span className={EM}>Answered.</span>
      </h1>
      <Sub>A corporate briefing for Global Market Access, HEOR and leadership teams preparing multi-market launches.</Sub>
      <div className="mt-14 flex items-end justify-between flex-wrap gap-6">
        <div>
          <p className="text-[11px] uppercase tracking-widest text-slate-500">An initiative by</p>
          <p className="font-heading font-bold text-slate-900 text-lg mt-1">MarksMan Healthcare</p>
        </div>
        <p className="text-[11px] text-slate-500">Confidential · For discussion purposes</p>
      </div>
    </div>
  )},
  { label: "The problem", render: () => (
    <div className="max-w-6xl grid grid-cols-1 lg:grid-cols-2 gap-10 items-center w-full">
      <div>
        <Overline>02 · The problem</Overline>
        <H>Evidence is everywhere.<br />Readiness is not.</H>
        <Sub>Trials, publications, value dossiers, economic models, payer feedback and affiliate answers live in different teams, folders and heads. Every market asks different questions — and the gaps surface only after submissions are already planned.</Sub>
        <div className="grid grid-cols-1 gap-2.5 mt-8">
          <Card title="Repeated work" body="The same evidence rebuilt independently, market after market." />
          <Card title="Gut-feel sequencing" body="Country priorities decided by precedent or the loudest affiliate." />
          <Card title="Knowledge that leaves" body="When people move on, what was submitted where — and why — goes with them." />
        </div>
      </div>
      <div className="hidden lg:flex justify-center"><ScatterGraphic /></div>
    </div>
  )},
  { label: "The shift", render: () => (
    <div className="max-w-6xl grid grid-cols-1 lg:grid-cols-2 gap-10 items-center w-full">
      <div>
        <Overline>03 · The shift</Overline>
        <H>Market access is becoming an evidence-operations discipline.</H>
        <Sub>EU Joint Clinical Assessment, diverging Asia-Pacific frameworks, shrinking launch windows and flat evidence budgets mean one thing: companies can no longer afford to treat readiness as a feeling. It has to be measured, governed and continuously maintained.</Sub>
        <p className="text-slate-700 text-base mt-8 border-l-2 border-emerald-500 pl-5">
          The organisations that win the next decade of launches will be the ones that can <span className="text-slate-900 font-semibold">prove</span> they are ready — market by market, evidence asset by evidence asset.
        </p>
      </div>
      <div className="hidden lg:flex justify-center"><TrendGraphic /></div>
    </div>
  )},
  { label: "What we do", render: () => (
    <div className="max-w-4xl">
      <Overline>04 · What we do</Overline>
      <H>AI-enabled. Expert-led.<br />Delivered as an engagement.</H>
      <Sub>MAI Matrix™ is MarksMan Healthcare's proprietary Market Access Intelligence Operating System. Our senior consultants use it to run your engagement — it is never sold as standalone software, and no raw AI output ever reaches your leadership.</Sub>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-10">
        <Card k="Not a repository" title="It reads your evidence" body="Every study, claim and model extracted, structured and human-verified." />
        <Card k="Not a dashboard" title="It computes readiness" body="Deterministic, auditable scores — recomputed the moment anything changes." />
        <Card k="Not a snapshot" title="It stays alive" body="Regulatory intelligence monitored continuously; every deliverable regenerable." />
      </div>
    </div>
  )},
  { label: "The framework", render: () => (
    <div className="max-w-6xl grid grid-cols-1 lg:grid-cols-2 gap-10 items-center w-full">
      <div>
        <Overline>05 · The framework</Overline>
        <H>GMARI<span className={EM}>™</span></H>
        <p className="text-slate-900 font-semibold text-lg mt-4">The Global Market Access Readiness Index.</p>
        <Sub className="mt-4">One governed 0–100 readiness verdict per market, decomposable across eight domains — from evidence completeness to submission readiness. Rule-based, comparable over time, defensible in any boardroom.</Sub>
        <div className="mt-8 space-y-3 max-w-md">
          {[["Evidence Completeness", 64, "#B45309"], ["Country Coverage", 38, "#B91C1C"], ["Evidence Reusability", 98, "#047857"], ["Submission Readiness", 72, "#047857"]].map(([l, v, c]) => (
            <div key={l}>
              <div className="flex justify-between text-xs mb-1"><span className="text-slate-700 font-semibold">{l}</span><span className="font-mono-data text-slate-600">{v}</span></div>
              <div className="h-1.5 bg-slate-200"><div className="h-full" style={{ width: `${v}%`, background: c }} /></div>
            </div>
          ))}
          <p className="text-[11px] text-slate-500 pt-1">Illustrative. Every weight and threshold is on the table in every engagement — no black-box scores.</p>
        </div>
      </div>
      <div className="hidden lg:flex justify-center"><RadarGraphic /></div>
    </div>
  )},
  { label: "How it works", render: () => (
    <div className="max-w-5xl">
      <Overline>06 · How it works</Overline>
      <H>From documents to decisions.<br />Four stages, each owned by the right layer.</H>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-10">
        <Card k="01 · AI" title="Ingest" body="AI reads your corpus — CSRs, publications, GVD, models, submissions — and proposes structured evidence and claims." />
        <Card k="02 · Experts" title="Verify" body="Every extracted fact is human-reviewed. Citations validated, designs classified, duplicates resolved. Nothing enters unverified." />
        <Card k="03 · Engine" title="Compute" body="Deterministic engines score readiness, map gaps, judge reusability across 12 dimensions and rank markets with honest rank ranges." />
        <Card k="04 · Consultants" title="Deliver" body="Senior consultants interpret, pressure-test and present the strategy — board-ready reports gated by formal release controls." />
      </div>
      <p className="text-slate-600 text-sm mt-8">AI-scale analysis · expert-validated interpretation · governance at every step.</p>
    </div>
  )},
  { label: "The engagement journey", render: () => (
    <div className="max-w-6xl grid grid-cols-1 lg:grid-cols-2 gap-10 items-center w-full">
      <div>
        <Overline>07 · The engagement journey</Overline>
        <H>Onboarding to board-ready in weeks — then continuously current.</H>
        <div className="grid grid-cols-2 gap-2.5 mt-8">
          {[["01", "Onboard & scope"], ["02", "Upload evidence"], ["03", "Verified inventory"], ["04", "GMARI™ readiness"], ["05", "Market prioritization"], ["06", "Gap analysis"], ["07", "Submission roadmap"], ["08", "Continuous monitoring"]].map(([n, t]) => (
            <div key={n} className="border border-slate-200 bg-white shadow-sm px-4 py-2.5 flex items-center gap-2.5">
              <span className={`font-mono-data text-[11px] ${EM}`}>{n}</span>
              <span className="text-sm text-slate-900 font-semibold">{t}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="hidden lg:block">
        <p className="text-[11px] uppercase tracking-widest text-slate-500 mb-3">Illustrative output · wave-sequenced submission roadmap</p>
        <WaveGraphic />
      </div>
    </div>
  )},
  { label: "Platform capabilities", render: () => (
    <div className="max-w-5xl">
      <Overline>08 · Under the hood</Overline>
      <H>Thirteen capabilities.<br />One operating system.</H>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5 mt-10">
        {["Knowledge Hub", "Document Ingestion", "Evidence Inventory", "Global Country Intelligence", "Competitive Intelligence", "Evidence Reusability", "Gap Analysis", "Market Prioritization", "Global Readiness · GMARI™", "Submission Planning", "Global Launch Strategy", "Intelligence Monitor", "AI Strategy Copilot"].map((c) => (
          <div key={c} className="border border-slate-200 bg-white shadow-sm px-4 py-3">
            <p className="text-sm text-slate-900 font-semibold">{c}</p>
          </div>
        ))}
      </div>
      <p className="text-slate-600 text-sm mt-6">Every capability draws on the same verified knowledge graph — change one input and every score, plan and report updates.</p>
    </div>
  )},
  { label: "For Market Access & Leadership", render: () => (
    <div className="max-w-4xl">
      <Overline>09 · For Market Access & Leadership</Overline>
      <H>A readiness verdict you can govern by.</H>
      <ul className="space-y-4 mt-10">
        <Li t="One answer to 'are we ready?'" d="GMARI™ per market, decomposable to every underlying input." />
        <Li t="Defensible country sequencing" d="six-dimension prioritization with sensitivity analysis and rank ranges, not false precision." />
        <Li t="Approved assumptions only" d="every commercial estimate carries its source, confidence and a senior sign-off before it reaches an external report." />
        <Li t="Board-ready deliverables" d="the three-part Readiness Report, generated from live data and gated by a formal release checklist." />
        <Li t="Institutional memory" d="what was decided, submitted and learned — retained beyond any individual." />
      </ul>
    </div>
  )},
  { label: "For HEOR & Evidence teams", render: () => (
    <div className="max-w-4xl">
      <Overline>10 · For HEOR & Evidence teams</Overline>
      <H>Every evidence euro spent where it unlocks markets.</H>
      <ul className="space-y-4 mt-10">
        <Li t="A verified evidence register" d="design-classified, citation-validated, quality-reviewed — duplicates caught, nothing silently lost." />
        <Li t="Twelve-dimension reuse verdicts" d="accepted as submitted, after adaptation, supportive only, or not acceptable — per requirement, per market. Global shells never masquerade as local evidence." />
        <Li t="Gap maps with leverage" d="which gaps block which submissions, and which single study unlocks the most markets." />
        <Li t="GVD alignment" d="section-by-section assessment against the current evidence base, with drafting support." />
        <Li t="Honest epidemiology" d="addressable populations built from explicit inputs — 'not estimable' when data is missing, never an invented zero." />
      </ul>
    </div>
  )},
  { label: "What teams receive", render: () => (
    <div className="max-w-5xl">
      <Overline>11 · What teams receive</Overline>
      <H>Decision-ready outputs.<br />One coherent engagement.</H>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-10">
        <Card k="01" title="Global Readiness Assessment" body="GMARI™ composite with eight-domain, per-country decomposition." />
        <Card k="02" title="Country Prioritization" body="Ranked markets with rank ranges, assumptions register and approval trail." />
        <Card k="03" title="Evidence Inventory & Gap Analysis" body="The verified register plus per-market gaps with closure plans." />
        <Card k="04" title="Evidence Reuse Assessment" body="What travels, what needs adaptation, what genuinely doesn't." />
        <Card k="05" title="Submission Roadmap & Launch Strategy" body="Wave-sequenced plans with minimum evidence packages and entry levers." />
        <Card k="06" title="The Readiness Report" body="Executive summary, technical report and country annexures — release-gated." />
      </div>
    </div>
  )},
  { label: "Governance & trust", render: () => (
    <div className="max-w-4xl">
      <Overline>12 · Governance & trust</Overline>
      <H>Nothing unverified reaches a payer — or your board.</H>
      <ul className="space-y-4 mt-10">
        <Li t="Deterministic where it matters" d="readiness, gaps and reuse are computed by transparent rules; AI never improvises a score." />
        <Li t="Provenance on everything" d="every claim traces to a source; every source carries a verification status; every input is labelled calculated, entered or estimated." />
        <Li t="Human approval gates" d="extracted facts, intelligence changes and commercial assumptions all pass senior review before they move a number." />
        <Li t="Release controls" d="external reports are blocked while critical claims are unverified — drafts carry explicit limitation registers." />
        <Li t="Secure by design" d="isolated client workspaces, role-based access, strict allowlists, full audit trails, no silent deletion." />
      </ul>
    </div>
  )},
  { label: "Why us", render: () => (
    <div className="max-w-6xl grid grid-cols-1 lg:grid-cols-2 gap-10 items-center w-full">
      <div>
        <Overline>13 · Why MarksMan</Overline>
        <H>Consulting that compounds.</H>
        <div className="grid grid-cols-1 gap-2.5 mt-8">
          <Card k="vs traditional consulting" title="A system, not a snapshot" body="The same senior expertise — plus a living intelligence base that keeps every deliverable current instead of aging from day one." />
          <Card k="vs repositories & search" title="Meaning, not storage" body="SharePoint and enterprise search find documents. We tell you what your evidence is worth in every market — and what to do next." />
          <Card k="vs generic AI" title="Governance is the product" body="Copilots have no market access model and will happily invent a number. Our scores are computed, labelled and approved." />
        </div>
        <p className="text-slate-700 text-sm mt-6 border-l-2 border-emerald-500 pl-5">
          Everyone else helps you find or store your evidence. <span className="text-slate-900 font-semibold">We tell you what it's worth — in every market, right now.</span>
        </p>
      </div>
      <div className="hidden lg:flex justify-center"><QuadrantGraphic /></div>
    </div>
  )},
  { label: "Begin a conversation", render: () => (
    <div className="max-w-4xl">
      <Overline>14 · Begin a conversation</Overline>
      <H>Confidential.<br />Non-obligatory.</H>
      <Sub>Scoping conversations are sized to the asset, indication, target markets and the state of your evidence base. No platform licence, no seat-based subscription — a scoped, expert-led engagement.</Sub>
      <p className={`font-heading font-bold text-lg mt-8 ${EM}`}>Evidence → Readiness → Strategy</p>
      <div className="mt-10 flex items-center gap-6 flex-wrap">
        <Link to="/request-demo" data-testid="deck-cta" onClick={() => track("deck_cta_click", "/deck")}
          className="bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold px-7 py-3.5 inline-flex items-center gap-2 transition-colors">
          Request a briefing <ArrowRight size={15} weight="bold" />
        </Link>
        <div>
          <p className="text-[11px] uppercase tracking-widest text-slate-500">An initiative by</p>
          <p className="font-heading font-bold text-slate-900 mt-0.5">MarksMan Healthcare</p>
        </div>
      </div>
      <div className="mt-14"><Wordmark /></div>
    </div>
  )},
];

export default function DeckPage() {
  const [i, setI] = useState(0);
  const touchX = useRef(null);
  const n = SLIDES.length;
  const go = useCallback((d) => setI((x) => Math.min(n - 1, Math.max(0, x + d))), [n]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === " " || e.key === "PageDown") { e.preventDefault(); go(1); }
      if (e.key === "ArrowLeft" || e.key === "PageUp") { e.preventDefault(); go(-1); }
      if (e.key === "Home") setI(0);
      if (e.key === "End") setI(n - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, n]);

  useEffect(() => { document.title = "MAI Matrix™ — Corporate Briefing"; }, []);
  const completed = useRef(false);
  useEffect(() => {
    if (i === n - 1 && !completed.current) { completed.current = true; track("deck_completed", "/deck", { slides: n }); }
  }, [i, n]);

  const slide = SLIDES[i];
  return (
    <div data-testid="deck-page" className="fixed inset-0 bg-[#F7F9FC] text-slate-900 flex flex-col select-none"
      onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        if (touchX.current === null) return;
        const dx = e.changedTouches[0].clientX - touchX.current;
        if (Math.abs(dx) > 60) go(dx < 0 ? 1 : -1);
        touchX.current = null;
      }}>
      <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: "linear-gradient(rgba(15,23,42,0.045) 1px, transparent 1px), linear-gradient(90deg, rgba(15,23,42,0.045) 1px, transparent 1px)", backgroundSize: "48px 48px" }} />

      {/* top bar */}
      <div className="relative flex items-center justify-between px-6 lg:px-12 pt-6 text-[11px] text-slate-500">
        <span data-testid="deck-label" className="uppercase tracking-widest">{String(i + 1).padStart(2, "0")} · {slide.label}</span>
        <span className="font-mono-data">{String(i + 1).padStart(2, "0")} / {n} <span className="ml-3 text-slate-600">← / → to navigate</span></span>
      </div>

      {/* slide body */}
      <div key={i} className="relative flex-1 flex items-center px-8 sm:px-14 lg:px-24 overflow-y-auto py-8 deck-slide-in">
        {slide.render()}
      </div>

      {/* click zones */}
      {i > 0 && <button data-testid="deck-prev" aria-label="Previous slide" onClick={() => go(-1)}
        className="absolute left-0 top-16 bottom-16 w-16 flex items-center justify-start pl-4 text-slate-600 hover:text-slate-900 transition-colors">
        <ArrowLeft size={18} /></button>}
      {i < n - 1 && <button data-testid="deck-next" aria-label="Next slide" onClick={() => go(1)}
        className="absolute right-0 top-16 bottom-16 w-16 flex items-center justify-end pr-4 text-slate-600 hover:text-slate-900 transition-colors">
        <ArrowRight size={18} /></button>}

      {/* progress */}
      <div className="relative px-6 lg:px-12 pb-6">
        <div className="flex items-center gap-1.5">
          {SLIDES.map((_, k) => (
            <button key={k} data-testid={`deck-dot-${k}`} aria-label={`Slide ${k + 1}`} onClick={() => setI(k)}
              className={`h-[3px] flex-1 transition-colors ${k <= i ? "bg-emerald-600" : "bg-slate-200 hover:bg-slate-400"}`} />
          ))}
        </div>
      </div>
      <style>{`.deck-slide-in{animation:deckIn .45s ease both}@keyframes deckIn{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:none}}`}</style>
    </div>
  );
}

